import AddPhotoAlternateRounded from '@mui/icons-material/AddPhotoAlternateRounded';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import ImageRounded from '@mui/icons-material/ImageRounded';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { ApiError, api, unwrap } from '../../api/client';
import { useAuth } from '../../auth/AuthProvider';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { EmptyState } from '../../components/states';
import { useNotify } from '../../lib/notify';
import {
  catalogKeys,
  fileSize,
  imageProblem,
  IMAGE_TYPES,
  MAX_IMAGES,
  type Product,
  type ProductImage,
} from './model';
import { forgetProductImage, useProductImageUrl } from './queries';

/** Upload raw image bytes (the API detects the type from the content). */
async function uploadImage(productId: string, file: File) {
  return unwrap(
    api.POST('/products/{id}/images', {
      params: { path: { id: productId } },
      body: file as unknown as string,
      bodySerializer: (body: unknown) => body as BodyInit,
      headers: { 'Content-Type': file.type || 'application/octet-stream' },
    }),
  );
}

/** Product photos: grid with upload (button or drop) and remove. The first image is the cover. */
export function ImagesPanel({ product }: { product: Product }) {
  const { can } = useAuth();
  const notify = useNotify();
  const qc = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [removing, setRemoving] = useState<ProductImage | null>(null);
  const canEdit = can('products.update');
  const images = [...product.images].sort((a, b) => a.sortOrder - b.sortOrder);
  const room = MAX_IMAGES - images.length;

  const upload = useMutation({
    mutationFn: async (files: File[]) => {
      let done = 0;
      for (const file of files) {
        await uploadImage(product.id, file);
        done++;
      }
      return done;
    },
    onSuccess: (done) =>
      notify.success(done === 1 ? 'Image added' : `${String(done)} images added`),
    onError: (e) =>
      notify.error(
        e instanceof ApiError && e.status === 413
          ? new ApiError(413, e.code, 'That image is larger than 5 MB.')
          : e,
      ),
    onSettled: () => qc.invalidateQueries({ queryKey: catalogKeys.product(product.id) }),
  });

  const pick = (list: FileList | null) => {
    if (!list || list.length === 0) return;
    const files = Array.from(list);
    const bad = files.map((f) => ({ f, problem: imageProblem(f) })).find((x) => x.problem);
    if (bad) {
      notify.info(`${bad.f.name}: ${bad.problem ?? ''}`);
      return;
    }
    if (files.length > room) {
      notify.info(
        `You can add ${String(room)} more image${room === 1 ? '' : 's'} (at most ${String(MAX_IMAGES)}).`,
      );
      return;
    }
    upload.mutate(files);
  };

  const hidden = (
    <input
      ref={input}
      type="file"
      hidden
      multiple
      accept={IMAGE_TYPES.join(',')}
      onChange={(e) => {
        pick(e.target.files);
        e.target.value = '';
      }}
    />
  );

  if (images.length === 0 && !upload.isPending)
    return (
      <Card
        onDragOver={(e) => {
          if (!canEdit) return;
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          if (canEdit) pick(e.dataTransfer.files);
        }}
        sx={{ outline: dragging ? 2 : 0, outlineColor: 'primary.main', outlineStyle: 'dashed' }}
      >
        {hidden}
        <EmptyState
          icon={<ImageRounded />}
          title="No photos yet"
          body="A clear photo helps staff pick the right item. Drop JPEG, PNG or WebP files here — up to 5 MB each."
          action={
            canEdit ? { label: 'Upload photos', onClick: () => input.current?.click() } : undefined
          }
        />
      </Card>
    );

  return (
    <Card>
      {hidden}
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Stack direction="row" sx={{ alignItems: 'center', mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
            {images.length} of {MAX_IMAGES} · the first photo is the cover
          </Typography>
          {canEdit && (
            <Button
              startIcon={<AddPhotoAlternateRounded />}
              onClick={() => input.current?.click()}
              disabled={room <= 0}
              loading={upload.isPending}
            >
              Upload
            </Button>
          )}
        </Stack>
        <Box
          onDragOver={(e) => {
            if (!canEdit) return;
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            if (canEdit) pick(e.dataTransfer.files);
          }}
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: {
              xs: 'repeat(2, 1fr)',
              sm: 'repeat(3, 1fr)',
              md: 'repeat(5, 1fr)',
            },
            borderRadius: '14px',
            outline: dragging ? 2 : 0,
            outlineStyle: 'dashed',
            outlineColor: 'primary.main',
            outlineOffset: 6,
          }}
        >
          {images.map((img, i) => (
            <ImageTile
              key={img.id}
              productId={product.id}
              image={img}
              cover={i === 0}
              onRemove={canEdit ? () => setRemoving(img) : undefined}
            />
          ))}
          {upload.isPending && (
            <Skeleton
              variant="rounded"
              sx={{ aspectRatio: '1', height: 'auto', borderRadius: '14px' }}
            />
          )}
        </Box>
      </CardContent>
      <ConfirmDialog
        open={!!removing}
        danger
        title="Remove this photo?"
        body="It is deleted from the product. You can upload it again later."
        confirmLabel="Remove"
        onConfirm={() => {
          const img = removing;
          if (!img) return Promise.resolve();
          return unwrap(
            api.DELETE('/products/{id}/images/{imageId}', {
              params: { path: { id: product.id, imageId: img.id } },
            }),
          )
            .then(async () => {
              notify.success('Photo removed');
              await qc.invalidateQueries({ queryKey: catalogKeys.product(product.id) });
              forgetProductImage(qc, img.id);
            })
            .catch((e: unknown) => notify.error(e));
        }}
        onClose={() => setRemoving(null)}
      />
    </Card>
  );
}

function ImageTile({
  productId,
  image,
  cover,
  onRemove,
}: {
  productId: string;
  image: ProductImage;
  cover: boolean;
  onRemove?: () => void;
}) {
  const { url, loading } = useProductImageUrl(productId, image.id);
  return (
    <Box
      sx={{
        position: 'relative',
        aspectRatio: '1',
        borderRadius: '14px',
        overflow: 'hidden',
        border: 1,
        borderColor: 'divider',
        bgcolor: 'action.hover',
        '&:hover .tile-actions, &:focus-within .tile-actions': { opacity: 1 },
      }}
    >
      {loading ? (
        <Skeleton variant="rectangular" sx={{ width: '100%', height: '100%' }} />
      ) : url ? (
        <Box
          component="img"
          src={url}
          alt=""
          sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      ) : (
        <Box sx={{ display: 'grid', placeItems: 'center', height: '100%', color: 'text.disabled' }}>
          <ImageRounded />
        </Box>
      )}
      {cover && (
        <Chip
          size="small"
          label="Cover"
          sx={{
            position: 'absolute',
            left: 8,
            top: 8,
            bgcolor: 'background.paper',
            fontWeight: 600,
          }}
        />
      )}
      <Stack
        className="tile-actions"
        direction="row"
        sx={{
          position: 'absolute',
          inset: 'auto 0 0 0',
          p: 1,
          alignItems: 'center',
          opacity: { xs: 1, md: 0 },
          transition: 'opacity 150ms ease',
          background: 'linear-gradient(transparent, rgba(0,0,0,0.55))',
          color: '#fff',
        }}
      >
        <Typography variant="caption" sx={{ flex: 1, fontWeight: 600 }}>
          {fileSize(image.sizeBytes)}
        </Typography>
        {onRemove && (
          <Tooltip title="Remove">
            <IconButton
              size="small"
              aria-label="Remove photo"
              onClick={onRemove}
              sx={{ color: '#fff' }}
            >
              <DeleteOutlineRounded fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    </Box>
  );
}
